import { useRef, useState } from "react";

/**
 * Debounced employee ID uniqueness checker.
 * @param {string} token - Auth token for API calls
 * @param {string|null} originalId - The employee's existing ID (null for new employees).
 *   When the typed value matches this, it's immediately marked "available"
 *   (the employee is keeping their own ID).
 */
const useEmpIdCheck = (token, originalId = null) => {
  const [empIdStatus, setEmpIdStatus] = useState(null);
  const debounceRef = useRef(null);

  const checkEmpId = (id) => {
    clearTimeout(debounceRef.current);
    if (!id || id.trim().length < 2) {
      setEmpIdStatus(null);
      return;
    }
    const normalized = id.trim().toUpperCase();
    // If the value is unchanged from the original, it's always valid
    if (originalId && normalized === originalId.trim().toUpperCase()) {
      setEmpIdStatus("available");
      return;
    }
    setEmpIdStatus("checking");
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/getSingleEmployee/${encodeURIComponent(normalized)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.status === 404) {
          setEmpIdStatus("available");
        } else if (res.ok) {
          setEmpIdStatus("taken");
        } else {
          setEmpIdStatus(null);
        }
      } catch {
        setEmpIdStatus(null);
      }
    }, 500);
  };

  return { empIdStatus, checkEmpId, setEmpIdStatus };
};

export default useEmpIdCheck;
